import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Calendar, Video, TrendingUp, Utensils, Bus, Trophy, Target, Award, Zap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getRealCurrentDate, isSameDay, isInFuture } from '@/utils/dateUtils';
import AICoachChatbot from '@/components/AICoachChatbot';
import TrialBanner from '@/components/TrialBanner';
import OnboardingBanner from '@/components/OnboardingBanner';
import TeamTipCard from '@/components/TeamTipCard';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user, onLogout }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    players: 0,
    schedules: 0,
    videos: 0,
    upcomingGames: 0
  });
  const [todayGames, setTodayGames] = useState([]);
  const [realDate, setRealDate] = useState(null);
  const [chatbotOpen, setChatbotOpen] = useState(true); // Auto-open on load
  const [chatbotMinimized, setChatbotMinimized] = useState(false);
  const [teamSummary, setTeamSummary] = useState(null);
  const [teamBranding, setTeamBranding] = useState(null);
  const [quickLinks, setQuickLinks] = useState({ meal_providers: [] });
  const [showMealMenu, setShowMealMenu] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [checklist, setChecklist] = useState([
    { id: 1, task: 'Take attendance', completed: false },
    { id: 2, task: 'Check equipment', completed: false },
    { id: 3, task: 'Confirm meal orders', completed: false },
    { id: 4, task: 'Verify transportation', completed: false },
    { id: 5, task: 'Review game plan with coaching staff', completed: false }
  ]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Fetch real date first
      const currentDate = await getRealCurrentDate();
      setRealDate(currentDate);

      const [playersRes, schedulesRes, videosRes, teamSummaryRes, brandingRes, quickLinksRes, activitiesRes] = await Promise.all([
        axios.get(`${API}/players?team_id=${user.team_id}`),
        axios.get(`${API}/schedules?team_id=${user.team_id}`),
        axios.get(`${API}/videos?team_id=${user.team_id}`),
        axios.get(`${API}/team-summary?team_id=${user.team_id}`),
        axios.get(`${API}/team-branding/${user.team_id}`),
        axios.get(`${API}/teams/${user.team_id}/quick-links`),
        axios.get(`${API}/activities?team_id=${user.team_id}&limit=10`)
      ]);

      // Use real date for comparisons
      const gamesToday = schedulesRes.data.filter(s => {
        return s.type === 'game' && isSameDay(s.date, currentDate);
      });

      setTodayGames(gamesToday);
      setTeamSummary(teamSummaryRes.data);
      setTeamBranding(brandingRes.data);
      setQuickLinks(quickLinksRes.data);
      setRecentActivities(activitiesRes.data || []);

      setStats({
        players: playersRes.data.length,
        schedules: schedulesRes.data.length,
        videos: videosRes.data.length,
        upcomingGames: schedulesRes.data.filter(s => 
          s.type === 'game' && isInFuture(s.date, currentDate)
        ).length
      });
    } catch (error) {
      toast.error('Failed to load dashboard data');
    }
  };

  const handleDownloadTransportationTemplate = async () => {
    try {
      const response = await axios.get(`${API}/templates/transportation?team_id=${user.team_id}`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'transportation_booking_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Transportation file downloaded!');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleOrderTeamMeal = () => {
    if (quickLinks.meal_providers && quickLinks.meal_providers.length > 0) {
      if (quickLinks.meal_providers.length === 1) {
        // Single provider - open directly
        window.open(quickLinks.meal_providers[0].url, '_blank');
      } else {
        // Multiple providers - toggle menu
        setShowMealMenu(!showMealMenu);
      }
    } else {
      toast.info('No meal providers configured. Go to Settings to add one.');
      setTimeout(() => navigate('/settings'), 1000);
    }
  };

  const openMealProvider = (url) => {
    window.open(url, '_blank');
    setShowMealMenu(false);
  };

  const toggleChecklistItem = (id) => {
    setChecklist(checklist.map(item => 
      item.id === id ? {...item, completed: !item.completed} : item
    ));
  };

  const statCards = [
    { title: 'Total Players', value: stats.players, icon: Users, color: 'text-primary' },
    { title: 'Scheduled Events', value: stats.schedules, icon: Calendar, color: 'text-accent' },
    { title: 'Video Library', value: stats.videos, icon: Video, color: 'text-primary' },
    { title: 'Upcoming Games', value: stats.upcomingGames, icon: TrendingUp, color: 'text-accent' },
  ];

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-8 space-y-8" data-testid="admin-dashboard">
        <div className="border-b border-border pb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              {/* Large Mascot Display */}
              {teamBranding?.mascot_url ? (
                <div className="w-20 h-20 rounded-xl overflow-hidden border-4 shadow-xl flex-shrink-0"
                     style={{ borderColor: teamBranding.primary_color || '#1d4ed8' }}>
                  <img 
                    src={`${BACKEND_URL}${teamBranding.mascot_url}`} 
                    alt={teamBranding.mascot_name || 'Team Mascot'} 
                    className="w-full h-full object-cover"
                    data-testid="header-mascot-image"
                  />
                </div>
              ) : (
                <div className="w-20 h-20 rounded-xl border-4 shadow-xl flex-shrink-0 flex items-center justify-center bg-primary/10 overflow-hidden"
                     style={{ borderColor: teamBranding?.primary_color || '#1d4ed8' }}>
                  <img src="/pac-logo.png" alt="Team" className="w-full h-full object-contain" data-testid="header-fallback-logo" />
                </div>
              )}
              <div>
                <h1 className="font-heading text-5xl font-black uppercase tracking-tighter text-primary">
                  {teamBranding?.mascot_name || 'Admin'} Dashboard
                </h1>
                <p className="text-muted-foreground mt-2">
                  Welcome back, {user.name}
                  {teamBranding?.school_name && <span className="text-primary font-medium"> • {teamBranding.school_name}</span>}
                </p>
              </div>
            </div>
          </div>
        </div>

        <TrialBanner teamId={user.team_id} />
        <OnboardingBanner />

        {/* Game Day Checklist - Only shown on game days */}
        {todayGames.length > 0 && (
          <Card className="border-2 border-accent bg-accent/5" data-testid="game-day-checklist">
            <CardHeader className="border-b bg-accent/10">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                    <Trophy className="h-6 w-6 text-accent" />
                    GAME DAY CHECKLIST
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    {todayGames.length} game{todayGames.length > 1 ? 's' : ''} today!
                  </p>
                </div>
                <div className="px-4 py-2 bg-accent text-accent-foreground font-bold rounded-sm">
                  {checklist.filter(i => i.completed).length}/{checklist.length} COMPLETE
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-3">
                  <h3 className="font-semibold uppercase text-sm text-muted-foreground">Today's Games</h3>
                  {todayGames.map((game) => (
                    <div key={game.id} className="p-4 bg-background rounded-lg border-l-4 border-primary">
                      <p className="font-bold text-lg">{game.title}</p>
                      <p className="text-sm text-muted-foreground">{new Date(game.date).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})} - {game.location}</p>
                    </div>
                  ))}
                </div>
                <div className="space-y-2">
                  <h3 className="font-semibold uppercase text-sm text-muted-foreground mb-3">Pre-Game Tasks</h3>
                  {checklist.map((item) => (
                    <div 
                      key={item.id}
                      className="flex items-center gap-3 p-3 bg-background rounded-md hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => toggleChecklistItem(item.id)}
                      data-testid={`checklist-item-${item.id}`}
                    >
                      <input 
                        type="checkbox" 
                        checked={item.completed}
                        onChange={() => {}}
                        className="h-5 w-5 rounded border-2 border-primary"
                      />
                      <span className={`flex-1 font-medium ${item.completed ? 'line-through text-muted-foreground' : ''}`}>
                        {item.task}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statCards.map((stat) => {
            const Icon = stat.icon;
            return (
              <Card key={stat.title} className="stat-card border-2 hover:border-primary/50 transition-colors" data-testid={`stat-${stat.title.toLowerCase().replace(/\s+/g, '-')}`}>
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs uppercase tracking-wider text-muted-foreground font-bold">{stat.title}</p>
                      <p className="text-4xl font-black font-heading mt-2">{stat.value}</p>
                    </div>
                    <Icon className={`h-12 w-12 ${stat.color} opacity-20`} />
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Team Summary Card - Auto-updates after every game */}
        <Card className="border-2 border-primary/30 bg-gradient-to-r from-primary/5 to-accent/5" data-testid="team-summary-card">
          <CardHeader className="border-b bg-primary/10">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                {/* Team Mascot Preview in Summary Card */}
                {teamBranding?.mascot_url ? (
                  <div className="w-14 h-14 rounded-lg overflow-hidden border-2 border-primary/30 shadow-lg bg-white flex-shrink-0">
                    <img 
                      src={`${BACKEND_URL}${teamBranding.mascot_url}`} 
                      alt={teamBranding.mascot_name || 'Team Mascot'} 
                      className="w-full h-full object-cover"
                      data-testid="summary-mascot-image"
                    />
                  </div>
                ) : (
                  <div className="w-14 h-14 rounded-lg border-2 border-primary/30 shadow-lg bg-primary/10 flex-shrink-0 flex items-center justify-center overflow-hidden">
                    <img src="/pac-logo.png" alt="Team" className="w-full h-full object-contain" />
                  </div>
                )}
                <div>
                  <CardTitle className="font-heading uppercase text-2xl flex items-center gap-2">
                    {teamBranding?.mascot_name ? `${teamBranding.mascot_name} Summary` : 'Team Summary'}
                  </CardTitle>
                  {teamBranding?.school_name && (
                    <p className="text-sm text-muted-foreground">{teamBranding.school_name}</p>
                  )}
                </div>
              </div>
              {teamSummary?.record?.games_played > 0 && (
                <div className="px-4 py-2 bg-primary text-primary-foreground font-black rounded-sm text-lg">
                  {teamSummary.record.wins}W - {teamSummary.record.losses}L
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {teamSummary?.record?.games_played > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Season Record & Averages */}
                <div className="space-y-4">
                  <h3 className="font-semibold uppercase text-sm text-muted-foreground border-b pb-2">Season Stats</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Win %</span>
                      <span className="font-bold text-lg">{teamSummary.record.win_percentage}%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">PPG</span>
                      <span className="font-bold text-lg">{teamSummary.averages.points_per_game}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">RPG</span>
                      <span className="font-bold text-lg">{teamSummary.averages.rebounds_per_game}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">APG</span>
                      <span className="font-bold text-lg">{teamSummary.averages.assists_per_game}</span>
                    </div>
                  </div>
                </div>

                {/* Top Performers */}
                <div className="space-y-4">
                  <h3 className="font-semibold uppercase text-sm text-muted-foreground border-b pb-2">Top Performers</h3>
                  <div className="space-y-3">
                    {teamSummary.top_performers.scorer && (
                      <div className="flex items-center gap-3 p-2 bg-background rounded-md">
                        <Target className="h-5 w-5 text-primary" />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">Leading Scorer</p>
                          <p className="font-semibold">{teamSummary.top_performers.scorer.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-primary">{teamSummary.top_performers.scorer.value} pts</p>
                          <p className="text-xs text-muted-foreground">{teamSummary.top_performers.scorer.avg} PPG</p>
                        </div>
                      </div>
                    )}
                    {teamSummary.top_performers.rebounder && (
                      <div className="flex items-center gap-3 p-2 bg-background rounded-md">
                        <Award className="h-5 w-5 text-accent" />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">Top Rebounder</p>
                          <p className="font-semibold">{teamSummary.top_performers.rebounder.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-accent">{teamSummary.top_performers.rebounder.value} reb</p>
                          <p className="text-xs text-muted-foreground">{teamSummary.top_performers.rebounder.avg} RPG</p>
                        </div>
                      </div>
                    )}
                    {teamSummary.top_performers.assist_leader && (
                      <div className="flex items-center gap-3 p-2 bg-background rounded-md">
                        <Zap className="h-5 w-5 text-green-500" />
                        <div className="flex-1">
                          <p className="text-xs text-muted-foreground">Assist Leader</p>
                          <p className="font-semibold">{teamSummary.top_performers.assist_leader.name}</p>
                        </div>
                        <div className="text-right">
                          <p className="font-bold text-green-500">{teamSummary.top_performers.assist_leader.value} ast</p>
                          <p className="text-xs text-muted-foreground">{teamSummary.top_performers.assist_leader.avg} APG</p>
                        </div>
                      </div>
                    )}
                    {!teamSummary.top_performers.scorer && !teamSummary.top_performers.rebounder && !teamSummary.top_performers.assist_leader && (
                      <p className="text-sm text-muted-foreground">No player stats recorded yet</p>
                    )}
                  </div>
                </div>

                {/* Last Game */}
                <div className="space-y-4">
                  <h3 className="font-semibold uppercase text-sm text-muted-foreground border-b pb-2">Last Game</h3>
                  {teamSummary.last_game ? (
                    <div className="p-4 bg-background rounded-lg border-l-4 border-primary">
                      <div className="flex items-center justify-between mb-2">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${teamSummary.last_game.result === 'W' ? 'bg-green-100 text-green-700' : teamSummary.last_game.result === 'L' ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-700'}`}>
                          {teamSummary.last_game.result === 'W' ? 'WIN' : teamSummary.last_game.result === 'L' ? 'LOSS' : 'TIE'}
                        </span>
                        <span className="text-xs text-muted-foreground">{teamSummary.last_game.game_date}</span>
                      </div>
                      <p className="font-bold text-lg">vs {teamSummary.last_game.opponent}</p>
                      <p className="text-2xl font-black text-primary mt-1">
                        {teamSummary.last_game.our_score} - {teamSummary.last_game.opponent_score}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No games played yet</p>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <Trophy className="h-12 w-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="text-muted-foreground">No completed games yet.</p>
                <p className="text-sm text-muted-foreground mt-1">Start tracking game stats to see your team summary here!</p>
              </div>
            )}
          </CardContent>
        </Card>

        <TeamTipCard user={user} />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl">Quick Actions</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-3">
              <Button className="w-full justify-start" data-testid="quick-add-player" onClick={() => navigate('/players?action=add')}>
                <Users className="h-4 w-4 mr-2" />
                Add New Player
              </Button>
              <Button className="w-full justify-start" data-testid="quick-schedule-event" onClick={() => navigate('/schedule?action=add')}>
                <Calendar className="h-4 w-4 mr-2" />
                Schedule Event
              </Button>
              <Button className="w-full justify-start" data-testid="quick-upload-video" onClick={() => navigate('/videos?action=upload')}>
                <Video className="h-4 w-4 mr-2" />
                Upload Game Film
              </Button>
              <div className="relative">
                <Button className="w-full justify-start" data-testid="quick-order-meal" onClick={handleOrderTeamMeal}>
                  <Utensils className="h-4 w-4 mr-2" />
                  {quickLinks.meal_providers?.length > 0 
                    ? (quickLinks.meal_providers.length === 1 
                        ? quickLinks.meal_providers[0].name 
                        : 'Order Team Meal')
                    : 'Order Team Meal'}
                </Button>
                {showMealMenu && quickLinks.meal_providers?.length > 1 && (
                  <div className="absolute left-0 right-0 mt-1 bg-white border rounded-md shadow-lg z-10">
                    {quickLinks.meal_providers.map((provider, index) => (
                      <button
                        key={index}
                        className="w-full px-4 py-2 text-left hover:bg-muted flex items-center gap-2"
                        onClick={() => openMealProvider(provider.url)}
                        data-testid={`meal-provider-${index}`}
                      >
                        <Utensils className="h-4 w-4" />
                        {provider.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button className="w-full justify-start" data-testid="quick-book-transport" onClick={handleDownloadTransportationTemplate}>
                <Bus className="h-4 w-4 mr-2" />
                Download Transportation Template
              </Button>
            </CardContent>
          </Card>

          <Card className="border-2">
            <CardHeader className="border-b bg-muted/30">
              <CardTitle className="font-heading uppercase text-2xl">Recent Activity</CardTitle>
            </CardHeader>
            <CardContent className="p-6">
              <div className="space-y-4" data-testid="recent-activity-list">
                {recentActivities.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No recent activity. Start adding players, scheduling events, or uploading videos!
                  </p>
                ) : (
                  recentActivities.map((activity, index) => {
                    const getActivityColor = (type) => {
                      if (type.includes('player')) return 'bg-green-500';
                      if (type.includes('schedule')) return 'bg-blue-500';
                      if (type.includes('video')) return 'bg-purple-500';
                      if (type.includes('game')) return 'bg-orange-500';
                      return 'bg-gray-500';
                    };
                    
                    const getTimeAgo = (dateStr) => {
                      const date = new Date(dateStr);
                      const now = new Date();
                      const diffMs = now - date;
                      const diffMins = Math.floor(diffMs / 60000);
                      const diffHours = Math.floor(diffMs / 3600000);
                      const diffDays = Math.floor(diffMs / 86400000);
                      
                      if (diffMins < 1) return 'Just now';
                      if (diffMins < 60) return `${diffMins}m ago`;
                      if (diffHours < 24) return `${diffHours}h ago`;
                      return `${diffDays}d ago`;
                    };
                    
                    return (
                      <div key={activity.id || index} className="flex items-start gap-3 p-3 bg-muted/50 rounded-md" data-testid={`activity-${index}`}>
                        <div className={`h-2 w-2 ${getActivityColor(activity.activity_type)} rounded-full mt-2`}></div>
                        <div className="flex-1">
                          <p className="text-sm font-semibold">{activity.title}</p>
                          <p className="text-xs text-muted-foreground">{activity.description}</p>
                        </div>
                        <span className="text-xs text-muted-foreground">{getTimeAgo(activity.created_at)}</span>
                      </div>
                    );
                  })
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card className="border-2">
          <CardHeader className="border-b bg-muted/30">
            <CardTitle className="font-heading uppercase text-2xl">Upcoming Events</CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <p className="text-sm text-muted-foreground">No upcoming events. Schedule your next practice or game!</p>
          </CardContent>
        </Card>
      </div>

      {/* AI Coach Chatbot */}
      <AICoachChatbot
        user={user}
        isOpen={chatbotOpen}
        onClose={() => setChatbotOpen(false)}
        onMinimize={() => setChatbotMinimized(!chatbotMinimized)}
        isMinimized={chatbotMinimized}
      />
    </Layout>
  );
}